/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { isNonEmptyArray } from '@apollo/client/utilities';
import {
  Combobox,
  InputBase,
  useMantineTheme,
  Text,
  rem,
  type ComboboxStore,
  Stack,
  Box,
} from '@mantine/core';
import { RiExpandUpDownLine } from '@remixicon/react';
import { cloneElement } from 'react';

import { input } from '@fai/friendli-suite/views/layout/product/basic-header.css';
import type { ProjectDetailComboBoxItems } from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/use-de-project-detail-combobox';
import OptionItem from '@fai/friendli-suite/views/layout/product/option-item';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { assertNonEmptyArray } from '@fai/util/types';

function MappedProjectDetailComboBoxItems(items: ProjectDetailComboBoxItems) {
  assertNonEmptyArray(items);

  return items.map((item, index) => {
    const key = `project-detail-combobox-${item.type}-${index}`;

    switch (item.type) {
      case 'optionItem': {
        return (
          <Box pr={rem(4)} pl={rem(4)} key={key}>
            <OptionItem {...item.props} />
          </Box>
        );
      }
      case 'optionList': {
        return (
          <Stack
            key={key}
            gap={0}
            h="100%"
            pb={rem(4)}
            mah={rem(384)}
            style={{ overflowY: 'scroll' }}
          >
            {isNonEmptyArray(item.props) && MappedProjectDetailComboBoxItems(item.props)}
          </Stack>
        );
      }
      case 'jsx': {
        return cloneElement(item.Component, { key });
      }

      default: {
        return null;
      }
    }
  });
}

export function ProjectDetailComboBox({
  children,
  label,
  combobox,
  handleOptionSubmit,
}: {
  children: ProjectDetailComboBoxItems;
  label?: string | null;
  combobox: ComboboxStore;
  handleOptionSubmit: (newValue: string) => void;
}) {
  const theme = useMantineTheme();

  return (
    <Combobox
      offset={{ mainAxis: 4 }}
      position="bottom-start"
      resetSelectionOnOptionHover={true}
      shadow={theme.pf.shadow['shadow-md']}
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleOptionSubmit}
    >
      <Combobox.Target>
        <InputBase
          classNames={{ input }}
          component="button"
          maw={rem(240)}
          pointer={true}
          px={rem(1)}
          rightSection={<RiExpandUpDownLine color={theme.pf.color.fg.neutral.default} size={16} />}
          rightSectionPointerEvents="none"
          type="button"
          onClick={() => combobox.toggleDropdown()}
        >
          <Text
            c={theme.pf.color.fg.neutral.strong}
            style={{ textWrap: 'nowrap' }}
            truncate={true}
            variant={TextVariants.TitleXs}
          >
            {label ?? '(project)'}
          </Text>
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown
        miw={rem(240)}
        p={0}
        pb={rem(4)}
        style={{
          borderRadius: theme.pf.layout['3xs'],
        }}
      >
        <Combobox.Options>{MappedProjectDetailComboBoxItems(children)}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
